import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import styles from './styles.module.css';
import { searchProducts } from '../../../actions';
import { showAlert } from '../../../../../../common/actions';
import { Button, Input, Dropdown, SearchableSelect } from '../../../../../../common/ui';

const QTY_UNITS = [
  { id: 1, name: 'pcs' },
  { id: 2, name: 'kg' },
  { id: 3, name: 'g' },
  { id: 4, name: 'l' },
  { id: 5, name: 'ml' },
  { id: 6, name: 'm' },
  { id: 7, name: 'cm' },
];

const DISCOUNT_TYPES = [
  { id: 1, value: 'TOTAL_AMOUNT', name: 'Total Amount' },
  { id: 2, value: 'PER_UNIT', name: 'Per Unit' },
];

const DISCOUNT_UNITS = [
  { id: 1, name: 'Rs' },
  { id: 2, name: '%' },
];

const unitOptions = QTY_UNITS.map((u) => ({ value: u.name, label: u.name }));
const discountTypeOptions = DISCOUNT_TYPES.map((d) => ({ value: d.value, label: d.name }));
const discountUnitOptions = DISCOUNT_UNITS.map((u) => ({ value: u.name, label: u.name }));

const AddItemPopup = ({
  supplierInvoiceState: { suggestProducts },
  searchProducts,
  showAlert,
  addStockToInvoice,
  isOpenAddInvoicePopup,
  setOpenAddInvoicePopup,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isUnitPriceDisabled, setUnitPriceDisabled] = useState(false);
  const [isTotalAmountDisabled, setTotalAmountDisabled] = useState(false);
  const [formData, setFormData] = useState({
    productId: null,
    unit: null,
    qty: null,
    unitPrice: null,
    discountType: null,
    discount: { unit: null, value: null },
    totalAmount: null,
    sellingPrice: null,
    sellingDiscount: { unit: null, value: null },
    sellingLoyaltyDiscount: { unit: null, value: null },
    sellingWholesaleDiscount: { unit: null, value: null },
  });

  const onchangeBuyingValue = ({
    unitPrice = formData.unitPrice || 0,
    quantity = formData.qty || 0,
    discountType = formData.discountType,
    discountValue = formData.discount.value || 0,
    discountUnit = formData.discount.unit,
    totalAmount = formData.totalAmount || 0,
    isTotalAmountEntered = isUnitPriceDisabled,
    isUnitPriceEntered = isTotalAmountDisabled,
  }) => {
    let totalDiscountAmount = 0;
    if (discountType === 'TOTAL_AMOUNT' && discountUnit === 'Rs') {
      totalDiscountAmount = discountValue;
    } else if (discountType === 'PER_UNIT' && discountUnit === 'Rs') {
      totalDiscountAmount = discountValue * quantity;
    } else if (discountUnit === '%') {
      if (isTotalAmountEntered) {
        totalDiscountAmount = (totalAmount / (100 - discountValue)) * discountValue;
      } else if (isUnitPriceEntered) {
        totalDiscountAmount = (unitPrice * quantity * discountValue) / 100.0;
      }
    }

    let calculatedUnitPrice;
    let calculatedTotalCost;
    if (isTotalAmountEntered && quantity > 0) {
      calculatedUnitPrice = ((totalAmount + totalDiscountAmount) / quantity).toFixed(2);
    } else if (isUnitPriceEntered) {
      calculatedTotalCost = unitPrice * quantity - totalDiscountAmount;
    }

    return {
      calculatedUnitPrice: calculatedUnitPrice ?? formData.unitPrice,
      calculatedTotalCost: calculatedTotalCost ?? formData.totalAmount,
    };
  };

  const onChangeUnitPrice = (value) => {
    const converted = parseFloat(value);
    if (value === '' || isNaN(converted)) {
      setTotalAmountDisabled(false);
      setFormData((prev) => ({ ...prev, unitPrice: null, totalAmount: null }));
      return;
    }
    setTotalAmountDisabled(true);
    const { calculatedTotalCost } = onchangeBuyingValue({
      unitPrice: converted,
      isTotalAmountEntered: false,
      isUnitPriceEntered: true,
    });
    setFormData((prev) => ({
      ...prev,
      unitPrice: converted,
      totalAmount: calculatedTotalCost,
    }));
  };

  const onChangeQuantity = (value) => {
    const converted = parseFloat(value);
    const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({ quantity: converted });
    setFormData((prev) => ({
      ...prev,
      qty: converted,
      unitPrice: calculatedUnitPrice,
      totalAmount: calculatedTotalCost,
    }));
  };

  const onChangeTotalAmount = (value) => {
    const converted = parseFloat(value);
    if (value === '' || isNaN(converted)) {
      setUnitPriceDisabled(false);
      setFormData((prev) => ({ ...prev, unitPrice: null, totalAmount: null }));
      return;
    }
    setUnitPriceDisabled(true);
    const { calculatedUnitPrice } = onchangeBuyingValue({
      totalAmount: converted,
      isTotalAmountEntered: true,
      isUnitPriceEntered: false,
    });
    setFormData((prev) => ({
      ...prev,
      totalAmount: converted,
      unitPrice: calculatedUnitPrice,
    }));
  };

  const onChangeDiscountValue = (value) => {
    const converted = parseFloat(value);
    const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({
      discountValue: isNaN(converted) ? 0 : converted,
    });
    setFormData((prev) => ({
      ...prev,
      discount: { ...prev.discount, value: isNaN(converted) ? null : converted },
      unitPrice: calculatedUnitPrice,
      totalAmount: calculatedTotalCost,
    }));
  };

  const onSubmit = () => {
    if (!formData.productId) {
      showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please Select a Product!' });
      return;
    }
    if (!formData.unitPrice) {
      showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please Enter an Unit Price!' });
      return;
    }
    if (!formData.qty) {
      showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please Enter a Quantity!' });
      return;
    }
    if (!formData.unit) {
      showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please Select an Unit!' });
      return;
    }
    if (!formData.totalAmount) {
      showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please Enter a Total Amount!' });
      return;
    }
    if (!formData.sellingPrice) {
      showAlert({ isAlertOpen: true, alertType: 'danger', alertMessage: 'Please Enter a Selling Price!' });
      return;
    }
    if (formData.discount.value != null && formData.discount.value !== '' && !formData.discountType) {
      showAlert({
        isAlertOpen: true,
        alertType: 'danger',
        alertMessage: 'You have entered a discount amount. Please select the Discount Type to continue!',
      });
      return;
    }
    addStockToInvoice({ ...formData, product: selectedProduct });
    setOpenAddInvoicePopup(false);
  };

  const handleClose = useCallback(() => setOpenAddInvoicePopup(false), [setOpenAddInvoicePopup]);

  useEffect(() => {
    if (!isOpenAddInvoicePopup) return;
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpenAddInvoicePopup, handleClose]);

  if (!isOpenAddInvoicePopup) return null;

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-labelledby="add-item-title">
      <div className={styles.overlay} onClick={handleClose} aria-hidden="true" />
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.panelHeader}>
          <h2 id="add-item-title" className={styles.panelTitle}>
            Add item
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className={styles.panelBody}>
          <div className={styles.productRow}>
            <div className={styles.productSelectWrap}>
              <SearchableSelect
                async
                label="Product"
                options={suggestProducts || []}
                getOptionLabel={(o) => [o.code, o.name].filter(Boolean).join(' – ') || o.barcode || '—'}
                getOptionValue={(o) => o.id}
                value={formData.productId}
                onChange={(id) => {
                  setFormData((prev) => ({ ...prev, productId: id }));
                  if (!id) setSelectedProduct(null);
                }}
                onSelect={(opt) => setSelectedProduct(opt)}
                onSearch={(keyword) => searchProducts(keyword)}
                placeholder="Search by name, code or barcode"
                minSearchLength={1}
              />
            </div>
            <div className={styles.selectedProductWrap}>
              <span className={styles.selectedProductName}>
                {selectedProduct ? selectedProduct.name : '—'}
              </span>
            </div>
          </div>

          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Buying</h3>
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <Input
                  label="Unit price"
                  type="text"
                  value={formData.unitPrice != null ? String(formData.unitPrice) : ''}
                  onChange={onChangeUnitPrice}
                  disabled={isUnitPriceDisabled}
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Quantity"
                  type="text"
                  value={formData.qty != null ? String(formData.qty) : ''}
                  onChange={onChangeQuantity}
                />
              </div>
              <div className={styles.fieldGroup}>
                <Dropdown
                  label="Unit"
                  options={unitOptions}
                  value={formData.unit}
                  onChange={(val) => setFormData((prev) => ({ ...prev, unit: val }))}
                  placeholder="Not selected"
                />
              </div>
              <div className={styles.fieldGroup}>
                <Dropdown
                  label="Discount type"
                  options={discountTypeOptions}
                  value={formData.discountType}
                  onChange={(val) => {
                    const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({
                      discountType: val,
                    });
                    setFormData((prev) => ({
                      ...prev,
                      discountType: val,
                      unitPrice: calculatedUnitPrice,
                      totalAmount: calculatedTotalCost,
                    }));
                  }}
                  placeholder="Not selected"
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Discount value"
                  type="text"
                  value={formData.discount.value != null ? String(formData.discount.value) : ''}
                  onChange={onChangeDiscountValue}
                />
              </div>
              <div className={styles.fieldGroup}>
                <Dropdown
                  label="Discount unit"
                  options={discountUnitOptions}
                  value={formData.discount.unit}
                  onChange={(val) => {
                    const { calculatedUnitPrice, calculatedTotalCost } = onchangeBuyingValue({
                      discountUnit: val,
                    });
                    setFormData((prev) => ({
                      ...prev,
                      discount: { ...prev.discount, unit: val },
                      unitPrice: calculatedUnitPrice,
                      totalAmount: calculatedTotalCost,
                    }));
                  }}
                  placeholder="Not selected"
                />
              </div>
              <div className={styles.fieldGroup}>
                <Input
                  label="Total amount"
                  type="text"
                  value={formData.totalAmount != null ? String(formData.totalAmount) : ''}
                  onChange={(val) => {
                    const num = parseFloat(val);
                    if (val === '' || isNaN(num)) {
                      setUnitPriceDisabled(false);
                      setFormData((prev) => ({ ...prev, unitPrice: null, totalAmount: null }));
                      return;
                    }
                    onChangeTotalAmount(val);
                  }}
                  disabled={isTotalAmountDisabled}
                />
              </div>
            </div>
          </section>

          <section className={styles.formSection}>
            <h3 className={styles.sectionTitle}>Selling</h3>
            <div className={styles.fieldGrid}>
              <div className={styles.fieldGroup}>
                <Input
                  label="Selling price"
                  type="text"
                  value={formData.sellingPrice != null ? String(formData.sellingPrice) : ''}
                  onChange={(value) => {
                    const num = parseFloat(value);
                    setFormData((prev) => ({ ...prev, sellingPrice: isNaN(num) ? null : num }));
                  }}
                />
              </div>
              <div className={`${styles.fieldGroup} ${styles.discountRow}`}>
                <div className={styles.discountField}>
                  <Input
                    label="Selling discount"
                    type="text"
                    value={formData.sellingDiscount.value != null ? String(formData.sellingDiscount.value) : ''}
                    onChange={(value) => {
                      const num = parseFloat(value);
                      setFormData((prev) => ({
                        ...prev,
                        sellingDiscount: { ...prev.sellingDiscount, value: isNaN(num) ? null : num },
                      }));
                    }}
                  />
                </div>
                <div className={styles.discountField}>
                  <Dropdown
                    label="Unit"
                    options={discountUnitOptions}
                    value={formData.sellingDiscount.unit}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        sellingDiscount: { ...prev.sellingDiscount, unit: val },
                      }))
                    }
                    placeholder="Not selected"
                  />
                </div>
              </div>
              <div className={`${styles.fieldGroup} ${styles.discountRow}`}>
                <div className={styles.discountField}>
                  <Input
                    label="Loyalty discount"
                    type="text"
                    value={
                      formData.sellingLoyaltyDiscount.value != null
                        ? String(formData.sellingLoyaltyDiscount.value)
                        : ''
                    }
                    onChange={(value) => {
                      const num = parseFloat(value);
                      setFormData((prev) => ({
                        ...prev,
                        sellingLoyaltyDiscount: {
                          ...prev.sellingLoyaltyDiscount,
                          value: isNaN(num) ? null : num,
                        },
                      }));
                    }}
                  />
                </div>
                <div className={styles.discountField}>
                  <Dropdown
                    label="Unit"
                    options={discountUnitOptions}
                    value={formData.sellingLoyaltyDiscount.unit}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        sellingLoyaltyDiscount: { ...prev.sellingLoyaltyDiscount, unit: val },
                      }))
                    }
                    placeholder="Not selected"
                  />
                </div>
              </div>
              <div className={`${styles.fieldGroup} ${styles.discountRow}`}>
                <div className={styles.discountField}>
                  <Input
                    label="Wholesale discount"
                    type="text"
                    value={
                      formData.sellingWholesaleDiscount.value != null
                        ? String(formData.sellingWholesaleDiscount.value)
                        : ''
                    }
                    onChange={(value) => {
                      const num = parseFloat(value);
                      setFormData((prev) => ({
                        ...prev,
                        sellingWholesaleDiscount: {
                          ...prev.sellingWholesaleDiscount,
                          value: isNaN(num) ? null : num,
                        },
                      }));
                    }}
                  />
                </div>
                <div className={styles.discountField}>
                  <Dropdown
                    label="Unit"
                    options={discountUnitOptions}
                    value={formData.sellingWholesaleDiscount.unit}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        sellingWholesaleDiscount: { ...prev.sellingWholesaleDiscount, unit: val },
                      }))
                    }
                    placeholder="Not selected"
                  />
                </div>
              </div>
            </div>
          </section>

          <div className={styles.buttonPanel}>
            <Button type="button" onClick={onSubmit}>
              Add item
            </Button>
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  supplierInvoiceState: state.supplierInvoiceState,
});

export default connect(mapStateToProps, {
  searchProducts,
  showAlert,
})(AddItemPopup);
